import { useTheme } from "@mui/material/styles";

import { themeColors, getColor, ColorPath } from "./colorTokens";

/**
 * Custom hook for accessing theme colors
 * Provides easy access to both MUI theme and our color tokens
 */
export const useCelleraTheme = () => {
  const muiTheme = useTheme();

  return {
    // MUI theme instance
    theme: muiTheme,

    // Our color tokens
    colors: themeColors,

    // Helper function to get colors by path
    getColor: (path: ColorPath) => getColor(path),

    // Commonly used colors for quick access
    primary: themeColors.primary.main,
    primaryDark: themeColors.primary.dark,
    primaryLight: themeColors.primary.light,
    secondary: themeColors.secondary.main,
    textPrimary: themeColors.text.primary,
    textSecondary: themeColors.text.secondary,
    background: themeColors.background.default,
    paper: themeColors.background.paper,

    // Utility functions
    spacing: muiTheme.spacing,
    breakpoints: muiTheme.breakpoints,
  };
};

/**
 * Hook for getting theme-aware styles
 * Returns commonly used sx props with theme colors
 */
export const useThemeStyles = () => {
  const { colors } = useCelleraTheme();

  return {
    // Card styles
    card: {
      backgroundColor: colors.background.paper,
      borderRadius: "12px",
      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
      padding: { xs: 3, sm: 4 },
    },

    // Button styles
    primaryButton: {
      backgroundColor: colors.primary.main,
      color: colors.primary.contrast,
      "&:hover": {
        backgroundColor: colors.primary.dark,
      },
    },

    // Input styles
    textField: {
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        backgroundColor: colors.background.paper,
        "& fieldset": {
          borderColor: colors.border.light,
        },
        "&:hover fieldset": {
          borderColor: colors.border.main,
        },
        "&.Mui-focused fieldset": {
          borderColor: colors.primary.main,
          borderWidth: "2px",
        },
      },
      "& .MuiInputLabel-root": {
        color: colors.text.secondary,
        "&.Mui-focused": {
          color: colors.primary.main,
        },
      },
    },
  };
};
